import React, {useEffect, useRef} from 'react';
import {useAuth} from '../../../features/auth/hooks/useAuth';
import {useAuthorization} from '../../../features/auth/hooks/useAuthorization';
import {AuthorizationService} from '../../../Services/authorization/AuthorizationService';
import {Navigate} from 'react-router-dom';
import Spinner from '../ui/Spinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  permission?: string;
  permissions?: string[];
  /** Gate to service-provider users (admins also allowed). Uses the same broad
   *  predicate as the post-login redirect, so providers can't be redirected
   *  to /provider and then rejected. */
  requireServiceProvider?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({children, requiredRole, permission, permissions, requireServiceProvider}) => {
  const {isAuthenticated, loading, initialized, initializeAuth} = useAuth();
  const {canAccessDashboard, hasPermission, hasAnyPermission, hasRole, isAdmin, user} = useAuthorization();
  const hasInitialized = useRef(false);


  // تهيئة المصادقة مرة واحدة فقط
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initializeAuth();
    }
  }, []); // لا نضع initializeAuth في dependencies

  // انتظار التهيئة أو الاستدعاء الشبكي
  if (!initialized || loading) {
    return <Spinner center size={40} />;
  }

  // بعد التهيئة: تحقق من حالة الدخول
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Dashboard access check
  const dashboard = canAccessDashboard();
  if (!dashboard.allowed) {
    return <Navigate to={dashboard.redirectTo || '/unauthorized'} replace />;
  }

  // Role-based authorization
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Service-provider-only route (admins also allowed)
  if (requireServiceProvider && !AuthorizationService.getInstance().isServiceProvider(user) && !isAdmin()) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Permission-based authorization
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Multiple permissions check
  if (permissions && !hasAnyPermission(permissions)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // All checks passed
  return <>{children}</>;
};

export default ProtectedRoute;
